import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * WorkspaceSharingModal — 工作区共享范围设置弹窗
 *
 * 在 AnyDev 一键创建工作区完成后自动弹出，允许把工作区共享给团队成员：
 *   - 可管理（managers）：完整配置/文件/Skill/成员权限 + 可使用工作区
 *   - 可使用（users）  ：仅查看配置，可通过智能体使用工作区
 *
 * 默认两组均仅含创建人。员工搜索 mock 后端：
 *   GET  /redir/api/crashclaw/listEmployees?keyword=
 *   POST /redir/api/crashclaw/setWorkspaceSharing
 *
 * 数据格式按 rtx 存储，提交时透传给后端。
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, message, Modal, Select, Spin, Tag } from 'antd';
import { TeamOutlined, CrownOutlined } from '@ant-design/icons';
import { ze } from 'utils/zhEn';
import RestHelper from 'utils/RestHelper';
import style from './style.scss';
const API_PREFIX = '/redir/api/crashclaw';
const SEARCH_DEBOUNCE_MS = 300;
function dedupeEmployees(list) {
    const seen = new Set();
    const out = [];
    for (const e of list) {
        if (!e?.rtx || seen.has(e.rtx))
            continue;
        seen.add(e.rtx);
        out.push(e);
    }
    return out;
}
function employeesToSelectOptions(employees) {
    return employees.map((e) => ({
        value: e.rtx,
        label: (_jsxs("span", { children: [_jsx("strong", { children: e.name }), _jsxs("span", { style: { color: '#86909c', marginLeft: 6 }, children: ["(", e.rtx, ")"] }), _jsx("span", { style: { color: '#c9cdd4', marginLeft: 6, fontSize: 12 }, children: e.dept })] })),
    }));
}
const WorkspaceSharingModal = ({ visible, workspaceUid, workspaceName, creatorRtx, mode = 'edit', onClose, }) => {
    const [managers, setManagers] = useState([]);
    const [users, setUsers] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [managerSearching, setManagerSearching] = useState(false);
    const [userSearching, setUserSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const searchSeqRef = useRef({ managers: 0, users: 0 });
    const searchTimerRef = useRef({
        managers: null,
        users: null,
    });
    function clearPendingSearches() {
        ['managers', 'users'].forEach((target) => {
            const pending = searchTimerRef.current[target];
            if (pending)
                clearTimeout(pending);
            searchTimerRef.current[target] = null;
        });
    }
    useEffect(() => {
        if (!visible) {
            setManagers([]);
            setUsers([]);
            setManagerOptions([]);
            setUserOptions([]);
            setManagerSearching(false);
            setUserSearching(false);
            searchSeqRef.current = { managers: 0, users: 0 };
            clearPendingSearches();
            return;
        }
        // 编辑模式：拉已有 sharing；创建模式：默认两组都仅含 creator
        (async () => {
            if (mode === 'edit' && workspaceUid) {
                try {
                    const rsp = await RestHelper.mustPost(`${API_PREFIX}/getWorkspaceSharing?workspaceUid=${encodeURIComponent(workspaceUid)}`);
                    const d = rsp.json.data || { managers: [], users: [] };
                    // 至少保留 creator 在两组中（兜底，避免后端历史脏数据丢失 owner）
                    const baseManagers = new Set(d.managers || []);
                    const baseUsers = new Set(d.users || []);
                    if (creatorRtx) {
                        baseManagers.add(creatorRtx);
                        baseUsers.add(creatorRtx);
                    }
                    setManagers(Array.from(baseManagers));
                    setUsers(Array.from(baseUsers));
                    return;
                }
                catch (e) {
                    console.error('Get workspace sharing failed', e);
                }
            }
            const initial = creatorRtx ? [creatorRtx] : [];
            setManagers(initial);
            setUsers(initial);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, creatorRtx, workspaceUid, mode]);
    const runSearch = useCallback(async (keyword, target) => {
        const seq = ++searchSeqRef.current[target];
        const setSearching = target === 'managers' ? setManagerSearching : setUserSearching;
        const setOptions = target === 'managers' ? setManagerOptions : setUserOptions;
        setSearching(true);
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/listEmployees?keyword=${encodeURIComponent(keyword)}`);
            if (seq !== searchSeqRef.current[target])
                return;
            const deduped = dedupeEmployees(rsp.json.data || []);
            setOptions(deduped);
        }
        catch (e) {
            if (seq === searchSeqRef.current[target]) {
                console.error('Search employees failed', e);
            }
        }
        finally {
            if (seq === searchSeqRef.current[target]) {
                setSearching(false);
            }
        }
    }, []);
    const scheduleSearch = useCallback((keyword, target) => {
        const pending = searchTimerRef.current[target];
        if (pending)
            clearTimeout(pending);
        searchTimerRef.current[target] = setTimeout(() => {
            searchTimerRef.current[target] = null;
            void runSearch(keyword, target);
        }, SEARCH_DEBOUNCE_MS);
    }, [runSearch]);
    const debouncedManagerSearch = useCallback((keyword) => scheduleSearch(keyword, 'managers'), [scheduleSearch]);
    const debouncedUserSearch = useCallback((keyword) => scheduleSearch(keyword, 'users'), [scheduleSearch]);
    useEffect(() => {
        const timers = searchTimerRef.current;
        return () => {
            if (timers.managers)
                clearTimeout(timers.managers);
            if (timers.users)
                clearTimeout(timers.users);
        };
    }, []);
    const managerSelectOptions = useMemo(() => employeesToSelectOptions(managerOptions), [managerOptions]);
    const userSelectOptions = useMemo(() => employeesToSelectOptions(userOptions), [userOptions]);
    async function handleSubmit() {
        setSubmitting(true);
        try {
            await RestHelper.mustPost(`${API_PREFIX}/setWorkspaceSharing`, {
                workspaceUid,
                managers,
                users,
            });
            message.success(ze('共享范围已保存', 'Sharing settings saved'));
            onClose();
        }
        catch (e) {
            console.error('Set workspace sharing failed', e);
            message.error(ze('保存失败，请稍后重试', 'Save failed, please try again'));
        }
        finally {
            setSubmitting(false);
        }
    }
    return (_jsx(Modal, { title: _jsxs("span", { children: [_jsx(TeamOutlined, { style: { color: '#ff7d00', marginRight: 8 } }), ze('编辑共享范围', 'Edit Sharing Scope')] }), open: visible, onCancel: onClose, width: 620, maskClosable: false, footer: _jsxs("div", { className: style.modalFooterRight, children: [_jsx(Button, { onClick: onClose, children: ze('稍后再说', 'Later') }), _jsx(Button, { type: "primary", loading: submitting, onClick: handleSubmit, children: ze('保存', 'Save') })] }), children: _jsxs("div", { className: style.sharingModalBody, children: [_jsx("div", { className: style.sharingModalHint, children: mode === 'created'
                        ? ze(`工作区「${workspaceName}」已创建成功。你可以邀请团队成员共同使用，仅支持添加员工。`, `Workspace "${workspaceName}" created successfully. Invite teammates to use it; only employees can be added.`)
                        : ze(`编辑工作区「${workspaceName}」的共享范围。仅支持添加公司员工。`, `Edit sharing scope for workspace "${workspaceName}". Only employees can be added.`) }), _jsxs("div", { className: style.sharingSection, children: [_jsxs("div", { className: style.sharingSectionHeader, children: [_jsx(CrownOutlined, { style: { color: '#ff7d00' } }), _jsx("span", { className: style.sharingSectionTitle, children: ze('可管理', 'Can Manage') }), _jsx(Tag, { color: "orange", className: style.sharingTag, children: ze('完整权限', 'Full Access') })] }), _jsx("div", { className: style.sharingSectionDesc, children: ze('管理工作区配置、文件、Skill、成员权限；可通过智能体使用工作区', 'Manage workspace config, files, skills, member permissions; can also use workspace via agents') }), _jsx(Select, { mode: "multiple", showSearch: true, value: managers, placeholder: ze('搜索并选择团队成员（按 RTX 或姓名）', 'Search by RTX or name'), filterOption: false, defaultActiveFirstOption: false, onSearch: debouncedManagerSearch, onDropdownVisibleChange: (open) => {
                                if (open)
                                    debouncedManagerSearch('');
                            }, onChange: setManagers, options: managerSelectOptions, style: { width: '100%' }, notFoundContent: managerSearching ? _jsx(Spin, { size: "small" }) : ze('无匹配员工', 'No matching employees') })] }), _jsxs("div", { className: style.sharingSection, children: [_jsxs("div", { className: style.sharingSectionHeader, children: [_jsx(TeamOutlined, { style: { color: '#165dff' } }), _jsx("span", { className: style.sharingSectionTitle, children: ze('可使用', 'Can Use') }), _jsx(Tag, { color: "blue", className: style.sharingTag, children: ze('只读 + 使用', 'Read + Use') })] }), _jsx("div", { className: style.sharingSectionDesc, children: ze('仅查看配置，可通过智能体使用工作区', 'View-only config, can use workspace via agents') }), _jsx(Select, { mode: "multiple", showSearch: true, value: users, placeholder: ze('搜索并选择团队成员（按 RTX 或姓名）', 'Search by RTX or name'), filterOption: false, defaultActiveFirstOption: false, onSearch: debouncedUserSearch, onDropdownVisibleChange: (open) => {
                                if (open)
                                    debouncedUserSearch('');
                            }, onChange: setUsers, options: userSelectOptions, style: { width: '100%' }, notFoundContent: userSearching ? _jsx(Spin, { size: "small" }) : ze('无匹配员工', 'No matching employees') })] })] }) }));
};
export default WorkspaceSharingModal;
